import dayjs, { Dayjs } from 'dayjs';
import { RRule, RRuleSet, rrulestr } from 'rrule';
import {
  AppointmentAgenda,
  AppointmentCoachAppointmentCoachRoleRetrieve,
  AppointmentWeek,
  DayAbbreviation,
  DayOfWeek,
  KiloClassCoachKiloClassCoachRoleRetrieve,
} from 'types';
import { KioskEventList } from 'types/generated';

export const updateRruleEndDate = (rrule: string, endDate: string | null): string => {
  let result = rrule;
  if (rrule.includes('UNTIL')) {
    const regex = /UNTIL=[0-9]*T[0-9]*Z;/;
    result = endDate ? rrule.replace(regex, `UNTIL=${endDate};`) : rrule.replace(regex, '');
  } else if (endDate) {
    const regForNewEndDate = /RRULE:/;
    result = rrule.replace(regForNewEndDate, `RRULE:UNTIL=${endDate};`);
  }
  return result;
};

export const updateRruleStartDate = (rrule: string, startDate: string | null): string => {
  const regex = /DTSTART:[0-9]*T[0-9]*Z\n/;
  const result = startDate ? rrule.replace(regex, `DTSTART:${startDate}\n`) : rrule.replace(regex, '');
  return result;
};

export const removeRruleExDates = (rrule: string): string => {
  const regex = /\nEXDATE:[0-9]*T[0-9]*Z/g;
  const result = rrule.replaceAll(regex, '');
  return result;
};

export const parseRruleForDays = (rrule: string | null | undefined): string[] => {
  const rruleset = rrule ? (rrulestr(rrule, { forceset: true }) as RRuleSet) : null;

  const dayArray = [];
  if (rruleset && rruleset._rrule[0].options.byweekday) {
    if (rruleset._rrule[0].options.byweekday.includes(6)) dayArray.push('SU');
    if (rruleset._rrule[0].options.byweekday.includes(0)) dayArray.push('MO');
    if (rruleset._rrule[0].options.byweekday.includes(1)) dayArray.push('TU');
    if (rruleset._rrule[0].options.byweekday.includes(2)) dayArray.push('WE');
    if (rruleset._rrule[0].options.byweekday.includes(3)) dayArray.push('TH');
    if (rruleset._rrule[0].options.byweekday.includes(4)) dayArray.push('FR');
    if (rruleset._rrule[0].options.byweekday.includes(5)) dayArray.push('SA');
  }
  return dayArray;
};

export const getDayAbbreviationFromDate = (date: string | Date): DayAbbreviation => {
  const dayArray: DayAbbreviation[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  return dayArray[dayjs(date).day()];
};

export const getDayFromDate = (date: string | Date): DayOfWeek => {
  const dayArray: DayOfWeek[] = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  return dayArray[dayjs(date).day()];
};

export const getDayAbbreviationsFromRruleBydays = (
  byDays: number[] | null | undefined,
): DayAbbreviation[] => {
  const byDayIndexToDay: { [key: number]: DayAbbreviation } = {
    0: 'MO',
    1: 'TU',
    2: 'WE',
    3: 'TH',
    4: 'FR',
    5: 'SA',
    6: 'SU',
  };
  // Monthly rrules (FREQ=MONTHLY;BYMONTHDAY=N) have no weekday byday, so rrule's
  // `options.byweekday` is null — return no weekday abbreviations rather than crashing.
  return (byDays ?? []).map(byDay => {
    return byDayIndexToDay[byDay];
  });
};

export type RecurrenceFrequency = 'weekly' | 'biweekly' | 'monthly';

// Inverse of createRrule's frequency encoding: FREQ=MONTHLY -> monthly,
// FREQ=WEEKLY;INTERVAL=2 -> biweekly, otherwise weekly. Used to prefill the
// recurrence-type selector when editing an existing series.
export const parseRruleForFrequency = (rrule: string | null | undefined): RecurrenceFrequency => {
  if (!rrule) {
    return 'weekly';
  }
  const options = (rrulestr(rrule, { forceset: true }) as RRuleSet).rrules()[0]?.options;
  if (!options) {
    return 'weekly';
  }
  if (options.freq === RRule.MONTHLY) {
    return 'monthly';
  }
  if (options.interval === 2) {
    return 'biweekly';
  }
  return 'weekly';
};

// Inverse of createRrule's monthly encoding: reads BYMONTHDAY back out of an rrule.
// Returns null for non-monthly / absent rules. Used to preserve the original anchor
// day when editing a series "all future" from a clamped occurrence (see createRrule).
export const parseRruleForMonthDay = (rrule: string | null | undefined): number | null => {
  if (!rrule) {
    return null;
  }
  const options = (rrulestr(rrule, { forceset: true }) as RRuleSet).rrules()[0]?.options;
  const byMonthDay = options?.bymonthday;
  if (!byMonthDay || byMonthDay.length === 0) {
    return null;
  }
  return byMonthDay[0];
};

export const createRrule = (
  startDate: Date,
  endDate: Date | null,
  days: string[],
  frequency: RecurrenceFrequency = 'weekly',
  monthDayAnchor?: number | null,
): string | null => {
  const dtstart = `DTSTART:${dayjs(startDate).format('YYYYMMDD')}T000000Z\n`;
  const until = endDate ? `;UNTIL=${dayjs(endDate).format('YYYYMMDD')}T000000Z` : '';

  // Monthly is day-of-month based (not weekday). The anchor day is `monthDayAnchor`
  // when supplied (the existing series' BYMONTHDAY, threaded through so an edit-all-
  // future from a *clamped* occurrence keeps the original day — e.g. editing a 31st
  // series from its Feb-28 occurrence stays on 31), else the start date's day for a
  // brand-new series. The backend clamps it to the month's last day for short months
  // (the 31st in February) — true calendar-month recurrence per PROD-104.
  if (frequency === 'monthly') {
    const anchorDay = monthDayAnchor ?? dayjs(startDate).date();
    return `${dtstart}RRULE:FREQ=MONTHLY;BYMONTHDAY=${anchorDay}${until}`;
  }

  // weekly / biweekly are weekday-based (BYDAY); biweekly repeats every 2 weeks.
  if (!days) {
    return null;
  }
  const interval = frequency === 'biweekly' ? ';INTERVAL=2' : '';
  return `${dtstart}RRULE:FREQ=WEEKLY${interval}${until};BYDAY=${days.join(',')}`;
};

export const calculateEventEndDateTime = (duration: number, startTime: string, startDate: Date): Dayjs => {
  const hour: number = Number(startTime.slice(0, 2));
  const minute: number = Number(startTime.slice(3, 5));
  const startDateTime = dayjs(
    new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), hour, minute),
  );
  return startDateTime.add(duration, 'minutes');
};

export const getDayDiff = (startDate: Dayjs, classEndDate: Dayjs): string => {
  const end = classEndDate.hour(0).minute(0).second(0);
  const start = startDate.hour(0).minute(0).second(0);
  const diff = Math.ceil(end.diff(start) / (1000 * 60 * 60 * 24));
  let dayDiff = '';
  if (diff === 1) {
    dayDiff = '+1 day';
  }
  if (diff > 1) {
    dayDiff = `+${diff} days`;
  }
  return dayDiff;
};

// Whether closing a create drawer should emit staff_event_create_abandoned: the user
// engaged with the form but nothing was created. Engagement is an explicit flag set by
// the interaction handlers — NOT Mantine's `isDirty()`, which is true before any
// interaction because the drawer re-seeds a fresh session uuid on open.
export const shouldTrackDrawerAbandonment = (submitted: boolean, engaged: boolean): boolean =>
  !submitted && engaged;

/** A per-row failure from a bulk-create response — `index` is the row's position
 *  in the submitted `rows` array. */
export interface BulkRowError {
  index: number;
  detail: string;
}

export interface BulkPartialFailure<TSession> {
  /** The sessions whose rows failed, in their original order — the ones to keep in the form. */
  survivingSessions: TSession[];
  /** Inline error message per surviving session, keyed by `sessionId`. */
  rowErrors: Record<string, string>;
  /** True when nothing was created (every row failed). */
  allFailed: boolean;
}

/**
 * Reconcile a bulk-create partial-failure response against the sessions that were
 * submitted: keep only the rows that failed, key a (generic, PII-safe) inline error
 * to each by `sessionId`, and report whether the whole batch failed.
 *
 * `errors[].index` is the row's position in the submitted array, so it aligns with
 * `sentSessions` index-for-index (both are built from the same ordered list).
 */
export const reconcileBulkPartialFailure = <TSession extends { sessionId: string }>(
  sentSessions: TSession[],
  errors: BulkRowError[],
  createdCount: number,
  rowErrorMessage: string,
): BulkPartialFailure<TSession> => {
  const failedIndexes = new Set(errors.map(rowError => rowError.index));
  const survivingSessions = sentSessions.filter((_, index) => failedIndexes.has(index));
  const rowErrors: Record<string, string> = {};
  sentSessions.forEach((session, index) => {
    if (failedIndexes.has(index)) {
      rowErrors[session.sessionId] = rowErrorMessage;
    }
  });
  return { survivingSessions, rowErrors, allFailed: createdCount === 0 };
};

export const getAppointmentEventTitle = (event: AppointmentAgenda | AppointmentWeek): string => {
  let title = '';
  const appName = event.appointmentType.name;
  if (event.athleteAppointments && event.athleteAppointments.length > 0) {
    const athlete = event.athleteAppointments[0].athlete;
    title = `${athlete.fullName}`;
    if (event.athleteAppointments.length > 1) {
      title += ` +${event.athleteAppointments.length - 1}`;
    }
    title += ` - ${appName}`;
    return title;
  }
  return 'Removed - ' + appName;
};

export const getFormattedCoachesNames = (event?: KioskEventList, threshold: number = 2): string => {
  if (!event) return '';

  const formatNames = (
    coachRoles: KiloClassCoachKiloClassCoachRoleRetrieve[] | AppointmentCoachAppointmentCoachRoleRetrieve[],
  ): string => {
    const coachNames = coachRoles.map(coachRole => coachRole.coach.fullName);
    if (coachNames.length > threshold) {
      const coachNamesToShow = coachNames.slice(0, threshold);
      const numberOfSubtractedCoaches = coachNames.slice(threshold);

      return `${coachNamesToShow.join(', ').toString()}, +${numberOfSubtractedCoaches.length}`;
    } else {
      return coachNames.join(', ').toString();
    }
  };

  switch (event.eventType) {
    case 'kilo_class_type':
      if (!event.kiloClassCoachKiloClassCoachRoles.length) return 'No Coach';
      return formatNames(event.kiloClassCoachKiloClassCoachRoles);

    case 'appointment_type':
      if (!event.appointmentCoachAppointmentCoachRoles.length) return 'No Coach';
      return formatNames(event.appointmentCoachAppointmentCoachRoles);
    default:
      return 'no coach found';
  }
};
